import React, { useEffect, useRef } from 'react';

import { STORYBOARD_FILMSTRIP, STORYBOARD_REVEAL } from '@/nova/theme/tokens';
import { cloudEdgeMask } from './BeatFilmstrip';
import { Holder } from './VideoReveal.styles';

/** Smootherstep: an S-curve with flat ends, so the sweep eases in and settles without a visible start or stop. */
export const smootherstep = (x: number): number => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * t * (t * (t * 6 - 15) + 10);
};

/**
 * Where the reveal's cloud edge is at progress p (0..1), in % of the width: from just left of the frame (nothing shown)
 * to a band past its right edge (the whole cloud band has passed, everything shown). Pure.
 */
export const revealEdge = (p: number): number => smootherstep(p) * (100 + STORYBOARD_FILMSTRIP.EDGE_BAND_PCT);

/** How the player shows: `off` (no hand-over: shown as is), `hidden` (a run just finished here: masked out until its
 *  first frame decodes, the run preview still over it) or `run` (the reveal plays). */
export type RevealState = 'off' | 'hidden' | 'run';

export interface VideoRevealProps {
  /** The finished ad's player. */
  children: React.ReactNode;
  state: RevealState;
  /** The reveal has finished (or was skipped under reduced motion): the run preview over it can go. */
  onDone: () => void;
}

/**
 * The hand-over from the run preview to the finished ad: the player materialises through the strip's own drifting
 * cloud edge sweeping left to right, pulling into focus as it settles, while the composer dissolves the run preview
 * (the strip and the dots) laid over it. An ad reopened rather than just rendered here plays as is (`off`).
 */
export const VideoReveal = ({ children, state, onDone }: VideoRevealProps): React.ReactElement => {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    // Syncs with the display's frame clock: masks, blurs and scales the player's holder while it reveals, then clears
    // every style it wrote. Fully masked while `hidden`, so only the run preview shows until the first frame decodes.
    const el = holderRef.current;
    if (!el || state === 'off') return undefined;
    const clear = (): void => {
      [
        'mask-image',
        '-webkit-mask-image',
        'mask-size',
        '-webkit-mask-size',
        'mask-position',
        '-webkit-mask-position',
        'mask-repeat',
        '-webkit-mask-repeat',
        'filter',
        'transform',
      ].forEach((prop) => el.style.removeProperty(prop));
    };
    const paint = (p: number, now: number): void => {
      const tile = STORYBOARD_FILMSTRIP.CLOUD_TILE_PX;
      const m = cloudEdgeMask(
        revealEdge(p),
        ((now / STORYBOARD_FILMSTRIP.CLOUD_A_LOOP_MS) * tile) % tile,
        ((now / STORYBOARD_FILMSTRIP.CLOUD_B_LOOP_MS) * tile) % tile,
      );
      el.style.setProperty('mask-image', m.image);
      el.style.setProperty('-webkit-mask-image', m.image);
      el.style.setProperty('mask-size', m.size);
      el.style.setProperty('-webkit-mask-size', m.size);
      el.style.setProperty('mask-position', m.position);
      el.style.setProperty('-webkit-mask-position', m.position);
      el.style.setProperty('mask-repeat', 'no-repeat, repeat-y, repeat-y');
      el.style.setProperty('-webkit-mask-repeat', 'no-repeat, repeat-y, repeat-y');
      const s = smootherstep(p);
      el.style.setProperty('filter', `blur(${(1 - s) * STORYBOARD_REVEAL.FOCUS_BLUR_PX}px)`);
      el.style.setProperty('transform', `scale(${1 + (1 - s) * (STORYBOARD_REVEAL.SETTLE_SCALE - 1)})`);
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      clear();
      if (state === 'run') onDoneRef.current();
      return undefined;
    }
    if (state === 'hidden') {
      paint(0, performance.now());
      // Leaving hidden (to off, when a new run starts before the first frame decodes, or to run) clears the mask: off
      // writes nothing, so without this the player stayed fully masked.
      return clear;
    }
    let raf = 0;
    let t0 = 0;
    // The sweep's first frame at once: the cleanup above has just cleared the hidden mask, and waiting for the next
    // frame would flash the player unmasked for one frame.
    paint(0, performance.now());
    const tick = (now: number): void => {
      if (!t0) t0 = now;
      const p = Math.min(1, (now - t0) / STORYBOARD_REVEAL.SWEEP_MS);
      paint(p, now);
      if (p < 1) {
        raf = window.requestAnimationFrame(tick);
        return;
      }
      clear();
      onDoneRef.current();
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
      clear();
    };
  }, [state]);
  return <Holder ref={holderRef}>{children}</Holder>;
};
